import { projectionToCsv, transactionsToCsv } from "../shared/csv";
import { utcTodayIso } from "../shared/dates";
import { inferBausparsummeFromTransactions } from "../shared/inferBausparsumme";
import { parseTransactionsFromText, transactionDateRange } from "../shared/pdfParse";
import { simulateBwz } from "../shared/simulate";
import {
  applyPayNowGuards,
  generateFutureTransactions,
  payNowTransaction,
  sortTransactions,
} from "../shared/transactions";
import type { BausparsummeInference } from "../shared/inferBausparsumme";
import type { CalculateSummary, ProjectionRow, Transaction } from "../shared/types";
import type { z } from "zod";
import {
  calculateBodySchema,
  MAX_PARSE_TEXT_CHARS,
  MAX_TRANSACTIONS,
  parseTextBodySchema,
  transactionSchema,
  type calculateParamsSchema,
} from "./schemas";

export type CalculateBody = z.infer<typeof calculateBodySchema>;
export type CalculateParams = z.infer<typeof calculateParamsSchema>;

export function parseText(text: string): {
  transactions: Transaction[];
  earliest: string;
  latest: string;
} & BausparsummeInference {
  const parsed = parseTextBodySchema.safeParse({ text });
  if (!parsed.success) {
    if (text.length > MAX_PARSE_TEXT_CHARS) {
      throw new Error("TEXT_TOO_LONG");
    }
    throw new Error("INVALID_TEXT");
  }

  const transactions = parseTransactionsFromText(parsed.data.text);
  if (transactions.length === 0) {
    throw new Error("NO_TRANSACTIONS");
  }
  if (transactions.length > MAX_TRANSACTIONS) {
    throw new Error("TOO_MANY_TRANSACTIONS");
  }

  const { earliest, latest } = transactionDateRange(transactions);
  const { suggested_bausparsumme, bausparsumme_hint } =
    inferBausparsummeFromTransactions(transactions);
  return { transactions, earliest, latest, suggested_bausparsumme, bausparsumme_hint };
}

export function suggestParams(transactions: Transaction[]) {
  if (transactions.length === 0) {
    throw new Error("No transactions provided");
  }
  const validated = transactions.map((tx) => transactionSchema.parse(tx));
  const { earliest, latest } = transactionDateRange(validated);
  const inference = inferBausparsummeFromTransactions(validated);
  return { earliest, latest, ...inference };
}

export function runCalculate(body: CalculateBody): {
  warnings: string[];
  summary: CalculateSummary;
  all_transactions: Transaction[];
  projection: ProjectionRow[];
  csv: { transactions: string; projection: string };
} {
  const validated = calculateBodySchema.parse(body);
  const { transactions: historical, params } = validated;
  const warnings: string[] = [];

  const { earliest: earliestTxDate, latest: latestTxDate } =
    transactionDateRange(historical);

  const futureTxs = generateFutureTransactions(latestTxDate, params.projection_end, {
    monthly_amount: params.monthly_payment,
    monthly_day: params.monthly_day,
    yearly_payment_1: params.yearly_payment_1,
    yearly_month_1: params.yearly_month_1,
    yearly_day_1: params.yearly_day_1,
    yearly_payment_2: params.yearly_payment_2,
    yearly_month_2: params.yearly_month_2,
    yearly_day_2: params.yearly_day_2,
    annual_fee: params.annual_fee,
    annual_fee_month: params.annual_fee_month,
    annual_fee_day: params.annual_fee_day,
  });

  const payNowDate = params.pay_now_date ?? params.as_of_date ?? utcTodayIso();
  const payNowRaw = payNowTransaction(params.pay_now_amount, payNowDate);
  const { tx: payNowTx, warnings: payNowWarnings } = applyPayNowGuards(
    payNowRaw,
    payNowDate,
    earliestTxDate,
    params.projection_end,
  );
  warnings.push(...payNowWarnings);

  const extra: Transaction[] = payNowTx ? [payNowTx] : [];
  const allTransactions = sortTransactions([...historical, ...extra, ...futureTxs]);

  const projection = simulateBwz(allTransactions, earliestTxDate, params.projection_end, {
    bausparsumme: params.bausparsumme,
    annual_interest_rate: params.annual_interest_rate_pct / 100,
    zinsfaktor: params.zinsfaktor,
    divisor_per_bausparsumme: params.divisor_per_bausparsumme,
    min_savings_ratio: params.min_savings_ratio_pct / 100,
    target_bwz: params.target_bwz,
  });

  if (projection.length === 0) {
    throw new Error("Projection produced no rows");
  }

  const final = projection[projection.length - 1];
  const firstReady = projection.find((r) => r.zuteilung_estimate_met);

  const summary: CalculateSummary = {
    end_date: final.date,
    balance: final.balance,
    cumulative_interest: final.cumulative_interest_for_bwz,
    bwz: final.bwz,
    min_savings_met: final.min_savings_met,
    target_bwz_met: final.target_bwz_met,
    first_zuteilung_date: firstReady?.date,
  };

  return {
    warnings,
    summary,
    all_transactions: allTransactions,
    projection,
    csv: {
      transactions: transactionsToCsv(allTransactions),
      projection: projectionToCsv(projection),
    },
  };
}
